import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
    Shield, Users, Zap, MessageSquare, 
    BookOpen, Calculator, Wallet, Receipt, 
    TrendingUp, Building2, Star, ChevronLeft, ChevronRight,
    ArrowRight
} from 'lucide-react';
import { useState, useRef } from 'react';
import { GlassCard, FloatingCard } from '../components/ui/GlassCard';

// Hero Section
const HeroSection = () => {
    return (
        <section 
            data-testid="hero-section" 
            className="relative min-h-screen flex items-center bg-hero-dark overflow-hidden"
        >
            {/* Background Shapes */}
            <div className="hero-shape hero-shape-1" />
            <div className="hero-shape hero-shape-2" />
            
            {/* Background Image Overlay */}
            <div 
                className="absolute inset-0 opacity-10"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1676390377080-b1b08f39c228?crop=entropy&cs=srgb&fm=jpg&q=85)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            />
            
            {/* Noise Texture */}
            <div className="absolute inset-0 noise-texture" />
            
            <div className="container-hope relative z-10 py-32">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    {/* Left Content */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20 mb-6"
                        >
                            <Shield className="w-4 h-4 text-hope-gold" />
                            <span className="text-white/90 text-sm font-medium">HMRC Compliant Services</span>
                        </motion.div>
                        
                        <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-tight mb-6">
                            Grow Your Business With{' '}
                            <span className="text-hope-gold">Premium</span>{' '}
                            Accounting Support
                        </h1>
                        
                        <p className="text-xl text-white/70 leading-relaxed mb-8 max-w-xl">
                            Modern accounting solutions helping UK businesses stay compliant, save time, and grow confidently.
                        </p>
                        
                        <div className="flex flex-wrap gap-4">
                            <Link
                                to="/contact"
                                data-testid="hero-cta-consultation"
                                className="btn-premium bg-white text-hope-emerald hover:bg-hope-light"
                            >
                                Book Free Consultation
                            </Link>
                            <Link
                                to="/services"
                                data-testid="hero-cta-services"
                                className="btn-premium btn-glass"
                            >
                                See Our Services
                            </Link>
                        </div>
                    </motion.div>
                    
                   {/* Right - Floating Mascot */}
<motion.div
  initial={{ opacity: 0, x: 50 }}
  animate={{ opacity: 1, x: 0 }}
  transition={{ duration: 0.8, delay: 0.3 }}
  className="hidden lg:flex justify-center items-center"
>
  <motion.div
    animate={{
      y: [0, -12, 0],
      rotate: [0, 1.5, -1.5, 0]
    }}
    transition={{
      duration: 5,
      repeat: Infinity,
      ease: "easeInOut"
    }}
    whileHover={{
      scale: 1.08,
      rotate: 2
    }}
    className="relative"
  >
    {/* Glow Effect */}
    <div className="absolute inset-0 blur-3xl bg-emerald-400/30 rounded-full scale-110"></div>

    {/* Mascot Image */}
    <img
      src="/Hope_Mascot.png"
      alt="Hope Accountancy Mascot"
      className="relative w-[420px] max-w-full drop-shadow-[0_25px_60px_rgba(0,0,0,0.45)]"
    />
  </motion.div>
</motion.div>
                </div>
            </div>
            
            {/* Bottom Wave */}
            <div className="absolute bottom-0 left-0 right-0">
                <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F1F8F4"/>
                </svg>
            </div>
        </section>
    );
};

// Trust Strip Section
const TrustStripSection = () => {
    const trustItems = [
        { icon: Shield, label: 'HMRC Compliant', desc: 'Fully regulated' },
        { icon: Users, label: 'Dedicated Accountant', desc: 'Personal support' },
        { icon: Zap, label: 'Fast Response', desc: 'Same-day replies' },
        { icon: MessageSquare, label: 'Transparent Communication', desc: 'No hidden fees' },
    ];

    return (
        <section data-testid="trust-strip-section" className="py-16 bg-hope-light relative">
            <div className="container-hope">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {trustItems.map((item, index) => (
                        <motion.div
                            key={item.label}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            data-testid={`trust-item-${index}`}
                            className="glass-card rounded-2xl p-6 text-center group hover:shadow-glass-lg transition-all duration-500"
                        >
                            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-hope-emerald/10 flex items-center justify-center group-hover:bg-hope-emerald group-hover:text-white transition-all duration-300">
                                <item.icon className="w-7 h-7 text-hope-emerald group-hover:text-white" />
                            </div>
                            <h3 className="font-heading font-semibold text-hope-forest mb-1">{item.label}</h3>
                            <p className="text-gray-500 text-sm">{item.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// Services Section
const ServicesSection = () => {
    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const services = [
        { 
            icon: BookOpen, 
            title: 'Bookkeeping', 
            desc: 'Accurate financial records maintained monthly with cloud-based solutions.',
            color: 'bg-emerald-500'
        },
        { 
            icon: Calculator, 
            title: 'Tax Returns', 
            desc: 'Personal and business tax returns filed accurately and on time.',
            color: 'bg-teal-500'
        },
        { 
            icon: Wallet, 
            title: 'Payroll Services', 
            desc: 'Complete payroll management including RTI submissions to HMRC.',
            color: 'bg-cyan-500'
        },
        { 
            icon: Receipt, 
            title: 'VAT Returns', 
            desc: 'Quarterly VAT calculations and submissions handled efficiently.',
            color: 'bg-green-500'
        },
        { 
            icon: TrendingUp, 
            title: 'Tax Planning', 
            desc: 'Strategic advice to minimize tax liability legally and ethically.',
            color: 'bg-emerald-600'
        },
        { 
            icon: Building2, 
            title: 'Company Accounts', 
            desc: 'Annual accounts preparation and Companies House filing.',
            color: 'bg-teal-600'
        },
    ];

    const scroll = (direction) => {
        if (scrollRef.current) {
            const scrollAmount = 340;
            scrollRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const handleScroll = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    return (
        <section data-testid="services-section" className="section-padding bg-hope-light relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-hope-soft/10 rounded-full blur-3xl" />
            
            <div className="container-hope relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center mb-16"
                >
                    <span className="inline-block px-4 py-2 rounded-full bg-hope-emerald/10 text-hope-emerald font-medium text-sm mb-4">
                        Our Services
                    </span>
                    <h2 className="font-heading font-bold text-4xl md:text-5xl text-hope-forest mb-4">
                        Premium Accounting Solutions
                    </h2>
                    <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                        Comprehensive financial services tailored to help your business thrive
                    </p>
                </motion.div>

                {/* Carousel Controls */}
                <div className="flex justify-end gap-3 mb-6">
                    <button
                        onClick={() => scroll('left')}
                        disabled={!canScrollLeft}
                        data-testid="services-scroll-left"
                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                            canScrollLeft 
                                ? 'bg-hope-emerald text-white hover:bg-hope-forest' 
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                        onClick={() => scroll('right')}
                        disabled={!canScrollRight}
                        data-testid="services-scroll-right"
                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                            canScrollRight 
                                ? 'bg-hope-emerald text-white hover:bg-hope-forest' 
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                    >
                        <ChevronRight className="w-6 h-6" />
                    </button>
                </div>

                {/* Services Carousel */}
                <div 
                    ref={scrollRef}
                    onScroll={handleScroll}
                    className="service-carousel -mx-6 px-6"
                >
                    {services.map((service, index) => (
                        <motion.div
                            key={service.title}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            data-testid={`service-card-${index}`}
                            className="w-[320px] bg-white rounded-3xl p-8 shadow-lg hover:shadow-floating transition-all duration-500 group border border-gray-100 hover:border-hope-emerald/20"
                        >
                            <div className={`w-16 h-16 rounded-2xl ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                <service.icon className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="font-heading font-semibold text-xl text-hope-forest mb-3">
                                {service.title}
                            </h3>
                            <p className="text-gray-600 leading-relaxed mb-6">
                                {service.desc}
                            </p>
                            <Link 
                                to="/services" 
                                className="inline-flex items-center gap-2 text-hope-emerald font-medium group-hover:gap-3 transition-all"
                            >
                                Learn More <ArrowRight className="w-4 h-4" />
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
    const benefits = [
        'Over 10 years of experience in UK accounting',
        'Dedicated accountant for personalized support',
        'Cloud-based solutions for real-time access',
        'Competitive and transparent pricing',
        'Proactive tax planning advice',
        'Quick response times guaranteed',
    ];

    return (
        <section data-testid="why-choose-section" className="section-padding bg-white relative overflow-hidden">
            <div className="container-hope">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Left Content */}
                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className="inline-block px-4 py-2 rounded-full bg-hope-emerald/10 text-hope-emerald font-medium text-sm mb-4">
                            Why Choose Us
                        </span>
                        <h2 className="font-heading font-bold text-4xl md:text-5xl text-hope-forest mb-6">
                            Premium Service You Can Trust
                        </h2>
                        <p className="text-gray-600 text-lg mb-8">
                            We combine expertise with personalized attention to deliver exceptional accounting services that help your business succeed.
                        </p>
                        
                        <ul className="space-y-4">
                            {benefits.map((benefit, index) => (
                                <motion.li
                                    key={index}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                    className="flex items-center gap-4"
                                >
                                    <div className="w-6 h-6 rounded-full bg-hope-soft flex items-center justify-center flex-shrink-0">
                                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span className="text-gray-700">{benefit}</span>
                                </motion.li>
                            ))}
                        </ul>
                    </motion.div>
                    
                    {/* Right - Rating Card */}
                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        className="relative"
                    >
                        {/* Background Image */}
                        <div className="absolute inset-0 rounded-3xl overflow-hidden">
                            <img 
                                src="https://images.unsplash.com/photo-1631248119882-c30225ac9904?crop=entropy&cs=srgb&fm=jpg&q=85"
                                alt="Modern office"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-hope-forest/90 to-hope-forest/40" />
                        </div>
                        
                        {/* Floating Rating Card */}
                        <div className="relative p-8 lg:p-12">
                            <FloatingCard 
                                className="bg-white/95 backdrop-blur-xl max-w-sm ml-auto mt-32"
                                data-testid="rating-card"
                            >
                                <div className="flex gap-1 mb-4">
                                    {[1,2,3,4,5].map((i) => (
                                        <Star key={i} className="w-8 h-8 text-hope-gold" fill="currentColor" />
                                    ))}
                                </div>
                                <p className="text-4xl font-heading font-bold text-hope-forest mb-2">5.0</p>
                                <p className="text-gray-600 mb-4">Average Client Rating</p>
                                <div className="flex items-center gap-3">
                                    <div className="flex -space-x-3">
                                        {['JD', 'SK', 'AM', 'RB'].map((initials, i) => (
                                            <div 
                                                key={i}
                                                className="w-10 h-10 rounded-full bg-hope-emerald flex items-center justify-center text-white text-sm font-medium border-2 border-white"
                                            >
                                                {initials}
                                            </div>
                                        ))}
                                    </div>
                                    <span className="text-gray-500 text-sm">500+ Happy Clients</span>
                                </div>
                            </FloatingCard>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

// Testimonials Section
const TestimonialsSection = () => {
    const testimonials = [
        {
            name: 'Sarah Mitchell',
            role: 'Restaurant Owner',
            image: null,
            initials: 'SM',
            quote: 'Hope Accountancy has been instrumental in managing our finances. Their expertise in restaurant accounting saved us thousands in taxes.',
        },
        {
            name: 'David Chen',
            role: 'E-commerce Entrepreneur',
            image: null,
            initials: 'DC',
            quote: 'Professional, responsive, and truly understanding of online business needs. They handle our VAT returns flawlessly.',
        },
        {
            name: 'Emma Thompson',
            role: 'Freelance Consultant',
            image: null,
            initials: 'ET',
            quote: 'As a freelancer, I needed someone who understood my unique situation. Hope Accountancy provides exactly that - personalized service.',
        },
    ];

    return (
        <section data-testid="testimonials-section" className="section-padding bg-hope-light relative overflow-hidden">
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-hope-emerald/5 rounded-full blur-3xl" />
            
            <div className="container-hope relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center mb-16"
                >
                    <span className="inline-block px-4 py-2 rounded-full bg-hope-emerald/10 text-hope-emerald font-medium text-sm mb-4">
                        Client Stories
                    </span>
                    <h2 className="font-heading font-bold text-4xl md:text-5xl text-hope-forest mb-4">
                        What Our Clients Say
                    </h2>
                    <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                        Don't just take our word for it - hear from businesses we've helped grow
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-3 gap-8">
                    {testimonials.map((testimonial, index) => (
                        <motion.div
                            key={testimonial.name}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.15 }}
                            data-testid={`testimonial-card-${index}`}
                            className="testimonial-card"
                        >
                            <div className="flex gap-1 mb-6">
                                {[1,2,3,4,5].map((i) => (
                                    <Star key={i} className="w-5 h-5 text-hope-gold" fill="currentColor" />
                                ))}
                            </div>
                            <p className="text-gray-700 leading-relaxed mb-6 italic">
                                "{testimonial.quote}"
                            </p>
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-hope-emerald flex items-center justify-center text-white font-semibold">
                                    {testimonial.initials}
                                </div>
                                <div>
                                    <p className="font-heading font-semibold text-hope-forest">{testimonial.name}</p>
                                    <p className="text-gray-500 text-sm">{testimonial.role}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// Final CTA Section
const CTASection = () => {
    return (
        <section data-testid="cta-section" className="section-padding bg-hero-dark relative overflow-hidden">
            {/* Background Shapes */}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-hope-soft/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-hope-emerald/20 rounded-full blur-3xl" />
            
            {/* Noise Texture */}
            <div className="absolute inset-0 noise-texture" />
            
            <div className="container-hope relative z-10 text-center">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <h2 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-6 max-w-4xl mx-auto">
                        Let us handle your accounting while you focus on growth
                    </h2>
                    <p className="text-white/70 text-xl mb-10 max-w-2xl mx-auto">
                        Join 500+ UK businesses who trust Hope Accountancy for their financial success
                    </p>
                    <Link
                        to="/contact"
                        data-testid="cta-consultation-btn"
                        className="btn-premium bg-white text-hope-emerald hover:bg-hope-light inline-flex items-center gap-3"
                    >
                        Book Free Consultation
                        <ArrowRight className="w-5 h-5" />
                    </Link>
                </motion.div>
            </div>
        </section>
    );
};

// Main HomePage Component
export default function HomePage() {
    return (
        <main data-testid="home-page">
            <HeroSection />
            <TrustStripSection />
            <ServicesSection />
            <WhyChooseUsSection />
            <TestimonialsSection />
            <CTASection />
        </main>
    );
}
